<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { AUTHMODELHS } from '@/utils/models'
import { useAuthStore } from '@/stores/auth.store'
import type { IRestPasswordRequest } from '@/services/auth.service'
import authService from '@/services/auth.service'
import otpService from '@/services/otp.service'
import utilModelsService from '@/services/util.models.service'
import closeIcon from '@/assets/images/svg/AuthOnboarding/CloseButton.svg'
import LoginRegisterBanner from '@/components/header/auth/AuthOnboardingAdvBanner/LoginRegisterBanner.vue'
import AuthOnboardingLabel from '@/components/header/auth/AuthOnboardingLabel.vue'
import Button from '@/components/atoms/button/index.vue'
import BaseInput from '@/components/forms/BaseInput.vue'
import ModelLayoutAuth from '@/layouts/ModelLayoutAuth.vue'
import { useField, useForm } from 'vee-validate'

import { string } from 'yup'

import { HttpStatusCode } from 'axios'
import { handleError } from '@/services/errorHandler.service'

const route = useRoute()
const authStore = useAuthStore()

const { errors, handleSubmit, resetForm } = useForm()

const { value: password, errorMessage: passwordError } = useField(
  'newPassword',
  string().required().min(5)
)
const { value: confirmPassword, errorMessage: confirmPasswordError } = useField(
  'retypeNewPassword',
  string().required().min(5)
)

const showRecaptcha = ref<boolean>(false)
const httpError = ref<string>('')
const resetError = ref('')
const inputTypePassword = ref<string>('password')
const inputTypeConfirmPassword = ref<string>('password')
const isLoading = ref<boolean>(false)

const showGoback = ref<boolean>(false)

const passwordNotMatchError = ref<string>('')
const togglePasswordEyeIcon = ref<boolean>(false)
const toggleConfirmPasswordEyeIcon = ref<boolean>(false)

const iconsToShow = computed(() => {
  if (togglePasswordEyeIcon.value) {
    return 'fa-regular fa-eye-slash'
  } else {
    return 'fa-regular fa-eye'
  }
})

const iconsToShowConfirm = computed(() => {
  if (toggleConfirmPasswordEyeIcon.value) {
    return 'fa-regular fa-eye-slash'
  } else {
    return 'fa-regular fa-eye'
  }
})
const canSubmit = computed(() => {
  return Object.keys(errors.value).length === 0 && !passwordNotMatchError.value
})

/**
 * If prop is true, page retrieves data again from API after changes from modal
 */
const handleLoadDataWhenClose = () => {
  authStore.setRedirectTo(null)
}

/**
 *  Toggle to show and hide password
 */
const handleShowPassword = () => {
  if (inputTypePassword.value === 'password') {
    inputTypePassword.value = 'text'
    togglePasswordEyeIcon.value = false
  } else {
    inputTypePassword.value = 'password'
    togglePasswordEyeIcon.value = true
  }
}

/**
 *  Toggle to show and hide password
 */
const handleShowConfirmPassword = () => {
  if (inputTypeConfirmPassword.value === 'password') {
    inputTypeConfirmPassword.value = 'text'
    toggleConfirmPasswordEyeIcon.value = false
  } else {
    inputTypeConfirmPassword.value = 'password'
    toggleConfirmPasswordEyeIcon.value = true
  }
}
/**
 * Close reset password model
 */
const closeResetPasswordModel = async () => {
  await authService.initAuth()
  utilModelsService.closeModal(AUTHMODELHS.LOGIN)
}

const onSubmit = handleSubmit(async (values) => {
  const requestPayload: IRestPasswordRequest = {
    username: authStore.userEmailOtpVerified!,
    otp: authStore.userEnteredOtp!,
    newPassword: values.newPassword,
    retypeNewPassword: values.retypeNewPassword
  }

  isLoading.value = true
  await otpService
    .resetForgotPasswordUsingOtp(requestPayload)
    .then((res) => {
      if (res?.status === HttpStatusCode.Ok) {
        resetError.value = ''
        httpError.value = ''
        resetForm()
        showGoback.value = true
      }
    })
    .catch((error: any) => {
      handleError(error)
    })
    .finally(() => {
      isLoading.value = false
    })
})

onMounted(async () => {
  await utilModelsService.onOpenModel((hs: string | null) => {
    if (hs === AUTHMODELHS.FORGOTPASSWORD && !showRecaptcha.value) {
      showRecaptcha.value = true
    }
  })

  watchEffect(() => {
    if (password.value !== confirmPassword.value) {
      passwordNotMatchError.value = 'Password does not match'
    } else {
      passwordNotMatchError.value = ''
    }
  })
})
onBeforeUnmount(() => {
  window.removeEventListener('open.hs.overlay', () => {})
})
</script>

<template>
  <ModelLayoutAuth :hs="AUTHMODELHS.RESETPASSWORD">
    <div class="flex h-screen w-full flex-col">
      <div class="absolute right-0 flex justify-end">
        <img
          :src="closeIcon"
          alt="Close"
          class="link-primary hs-collapse-toggle mr-5 mt-2 h-[20px] w-[20px] cursor-pointer transition-all"
          :data-hs-overlay="`#${AUTHMODELHS.RESETPASSWORD}`"
          @click="handleLoadDataWhenClose"
          v-show="!(route.name === 'extensionLogin' && !route.query.register)"
        />
      </div>
      <div class="flex w-full flex-1 p-1 sm:p-5">
        <div class="hidden w-full rounded-3xl bg-[#EBEFF6] md:hidden lg:block lg:min-w-[45%]">
          <LoginRegisterBanner
            text="A feature-rich coding suite powered by AI"
            highlighertext="powered by AI"
          />
        </div>

        <div class="mt-10 h-full w-full rounded-md sm:mt-8 lg:min-w-[50%]">
          <div class="flex items-center justify-center">
            <AuthOnboardingLabel
              title="Set your new password"
              customstyleTitle="text-center text-3xl font-medium leading-10"
            />
          </div>

          <div class="p-5">
            <div v-if="showGoback" class="mt-10 flex flex-col gap-x-5">
              <div class="flex flex-row items-center justify-center">
                <FontAwesomeIcon :icon="'circle-check'" class="mt-1 h-6 w-6 flex-shrink-0" />
                <div>
                  <h3 class="heading-small">Your password has been reset successfully</h3>
                </div>
              </div>
              <div
                class="mt-10 text-center text-sm font-medium leading-tight text-sky-950 dark:text-neutral-300"
              >
                <Button
                  color="secondary"
                  size="medium"
                  variant="filled"
                  class="mt-6 h-[48px] sm:w-[171px]"
                  @click="closeResetPasswordModel"
                >
                  Go to Homepage</Button
                >
              </div>
            </div>
            <form v-else @submit.prevent="onSubmit" class="w-[90%]">
              <div class="flex w-full flex-col items-center justify-center">
                <div class="w-full lg:w-[500px]">
                  <BaseInput
                    label="New Password"
                    :inputType="inputTypePassword"
                    placeholder="*****"
                    :error="passwordError"
                    v-model="password"
                    :isBoldLabel="true"
                    :isPassword="true"
                    :isPasswordIcon="iconsToShow"
                    @click="handleShowPassword"
                  />

                  <BaseInput
                    id="reset-pass"
                    label="Confirm Password"
                    :inputType="inputTypeConfirmPassword"
                    placeholder="*****"
                    :error="confirmPasswordError"
                    v-model="confirmPassword"
                    :isBoldLabel="true"
                    :isPassword="true"
                    :isPasswordIcon="iconsToShowConfirm"
                    @click="handleShowConfirmPassword"
                  />
                  <p :class="['mt-2', 'p-small', 'error', { hidden: !passwordNotMatchError }]">
                    {{ passwordNotMatchError }}
                  </p>
                  <p :class="['mt-5', 'p-small', 'text-center', 'text-sky-950']" v-if="isLoading">
                    Updating your password. Please wait...
                  </p>
                  <p :class="['mt-2', 'p-small', 'error', { hidden: !httpError }]">
                    {{ httpError }}
                  </p>
                </div>
              </div>

              <div class="flex justify-center sm:mt-9">
                <Button
                  :disabled="!canSubmit"
                  color="secondary"
                  size="medium"
                  variant="filled"
                  class="mt-6 h-[48px] sm:w-[171px]"
                  type="submit"
                >
                  Reset Password</Button
                >
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <!-- form -->
  </ModelLayoutAuth>
</template>
